import { createSlice } from '@reduxjs/toolkit';

import * as asyncThunks from './async-thunks';
import type { AuthState, LoginState } from './types';

const initialState: AuthState = {
  loginState: 2 as LoginState.NOT_LOGGED
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLoginError(state) {
      state.loginError = null;
    },
    clearRegistrationError(state) {
      state.registrationError = null;
    },
    clearResetPasswordError(state) {
      state.resetPasswordError = null;
    },
    clearChangePasswordError(state) {
      state.changePasswordError = null;
    },
    clearForgotPasswordError(state) {
      state.forgotPasswordError = null;
    },
    clearIsChangePasswordSuccess(state) {
      state.isChangePasswordSuccess = false;
    },
    clearIsRegistrationSuccess(state) {
      state.isRegistrationSuccess = false;
    },
    clearIsForgotPasswordSuccess(state) {
      state.isForgotPasswordSuccess = false;
    },
    setLoggedIn(state) {
      state.loginState = 0 as LoginState.LOGGED;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(asyncThunks.login.pending, state => {
        state.loginState = 1 as LoginState.PENDING;
        state.loginError = null;
      })
      .addCase(asyncThunks.login.fulfilled, state => {
        state.loginState = 0 as LoginState.LOGGED;
        state.loginError = null;
      })
      .addCase(asyncThunks.login.rejected, (state, action: any) => {
        state.loginState = 3 as LoginState.LOGIN_REJECTED;
        state.loginError = action.payload?.errorTranslationCode ?? 'Login failed';
      })
      .addCase(asyncThunks.loginAndJoinToCourse.pending, state => {
        state.loginState = 1 as LoginState.PENDING;
        state.loginError = null;
      })
      .addCase(asyncThunks.loginAndJoinToCourse.fulfilled, state => {
        state.loginState = 0 as LoginState.LOGGED;
        state.loginError = null;
      })
      .addCase(asyncThunks.loginAndJoinToCourse.rejected, (state, action: any) => {
        state.loginState = 3 as LoginState.LOGIN_REJECTED;
        state.loginError = action.payload?.errorTranslationCode ?? 'Login failed';
      })
      .addCase(asyncThunks.logInOrSignUpViaGoogle.pending, state => {
        state.loginState = 1 as LoginState.PENDING;
        state.loginError = null;
      })
      .addCase(asyncThunks.logInOrSignUpViaGoogle.fulfilled, state => {
        state.loginState = 0 as LoginState.LOGGED;
        state.loginError = null;
      })
      .addCase(asyncThunks.logInOrSignUpViaGoogle.rejected, (state, action: any) => {
        state.loginState = 3 as LoginState.LOGIN_REJECTED;
        state.loginError = action.payload?.errorTranslationCode ?? 'Login failed';
      })
      .addCase(asyncThunks.logInOrSignUpViaLinkedIn.pending, state => {
        state.loginState = 1 as LoginState.PENDING;
        state.loginError = null;
      })
      .addCase(asyncThunks.logInOrSignUpViaLinkedIn.fulfilled, state => {
        state.loginState = 0 as LoginState.LOGGED;
        state.loginError = null;
      })
      .addCase(asyncThunks.logInOrSignUpViaLinkedIn.rejected, (state, action: any) => {
        state.loginState = 3 as LoginState.LOGIN_REJECTED;
        state.loginError = action.payload?.errorTranslationCode ?? 'Login failed';
      })
      .addCase(asyncThunks.register.fulfilled, state => {
        state.isRegistrationSuccess = true;
        state.registrationError = null;
      })
      .addCase(asyncThunks.register.rejected, (state, action: any) => {
        state.registrationError = action.payload?.errorTranslationCode ?? 'Registration failed';
      })
      .addCase(asyncThunks.registerAndJoinToCourse.pending, state => {
        state.loginState = 1 as LoginState.PENDING;
        state.registrationError = null;
      })
      .addCase(asyncThunks.registerAndJoinToCourse.fulfilled, state => {
        state.loginState = 0 as LoginState.LOGGED;
        state.registrationError = null;
      })
      .addCase(asyncThunks.registerAndJoinToCourse.rejected, (state, action: any) => {
        state.loginState = 3 as LoginState.LOGIN_REJECTED;
        state.registrationError = action.payload?.errorTranslationCode ?? 'Registration failed';
      })
      .addCase(asyncThunks.resetPassword.rejected, (state, action: any) => {
        state.resetPasswordError = action.payload?.errorTranslationCode ?? 'Reset password failed';
      })
      .addCase(asyncThunks.forgotPassword.fulfilled, state => {
        state.isForgotPasswordSuccess = true;
      })
      .addCase(asyncThunks.forgotPassword.rejected, (state, action: any) => {
        state.isForgotPasswordSuccess = false;
        state.forgotPasswordError =
          action.payload?.errorTranslationCode ?? 'auth.forgotPasswordFailed';
      })
      .addCase(asyncThunks.changePassword.fulfilled, state => {
        state.isChangePasswordSuccess = true;
        state.changePasswordError = null;
      })
      .addCase(asyncThunks.changePassword.rejected, (state, action: any) => {
        state.changePasswordError =
          action.payload?.errorTranslationCode ?? 'Change password failed';
      })
      .addCase(asyncThunks.logout.pending, state => {
        state.loginState = 5 as LoginState.LOGGING_OUT;
      })
      .addCase(asyncThunks.logout.fulfilled, state => {
        state.loginState = 2 as LoginState.NOT_LOGGED;
        state.loginError = null;
        state.registrationError = null;
        state.resetPasswordError = null;
        state.changePasswordError = null;
        state.forgotPasswordError = null;
        state.isChangePasswordSuccess = false;
        state.isRegistrationSuccess = false;
      })
      .addCase(asyncThunks.logout.rejected, state => {
        // If logout fails, still set as not logged in to prevent loops
        state.loginState = 2 as LoginState.NOT_LOGGED;
      });
  }
});

export const {
  clearLoginError,
  clearRegistrationError,
  clearResetPasswordError,
  clearChangePasswordError,
  clearForgotPasswordError,
  clearIsChangePasswordSuccess,
  clearIsRegistrationSuccess,
  clearIsForgotPasswordSuccess,
  setLoggedIn
} = authSlice.actions;

export const authReducer = authSlice.reducer;
